import React, {useContext, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import FormField, {Props} from '@src/components/Form/FormField';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IsOrderCompletelyPayedItemField} from './items/IsOrderCompletelyPayedItemField';
import {TitleField} from './items/TitleField';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {IChecklistModel} from '@src/@interfaces/models/Checklist/IChecklistModel';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.CUSTOM,
        invisible: true,
    },
    {
        name: 'title',
        label: 'ChecklistsShow.fields.item.title',
        type: ListConfigFieldTypes.TEXT,
    },
    {
        name: 'is_order_completely_payed_item',
        label: 'ChecklistsShow.fields.item.is_order_completely_payed_item',
        type: ListConfigFieldTypes.SWITCH,
    },
];

function getCustomColumnComponent(config: ListConfigNamedColumn) {
    switch (config.name) {
        case 'id':
            return null;

        case 'title':
            return TitleField;

        case 'is_order_completely_payed_item':
            return IsOrderCompletelyPayedItemField;
    }

    return null;
}

function ActualForm(props: Props<IChecklistModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control} = useFormContext<IChecklistModel>();
    const {fields, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'items',
        keyName: 'autoGeneratedUuid',
    });

    const tabName = props.tabName;
    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length)
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const listContentProps: ControlledListProps<IChecklistModel>['listContentProps'] = {
            tableHeadProps: {
                additionalControlNode: <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        disabled={isDisabled}
                        onClick={() => append({
                            id: null,
                            title: undefined,
                            is_order_completely_payed_item: false,
                        })}
                >
                    <i className="fa fa-plus"/>
                </button>,
            },
            tableBodyProps: {
                renderControls(record, index) {
                    if (record?.id) {
                        return <ConfirmableButton
                                size="sm"
                                color="danger"
                                disabled={isDisabled}
                                onClick={() => remove(index)}
                        >
                            <i className="fa fa-times"/>
                        </ConfirmableButton>;
                    }

                    return <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            disabled={isDisabled}
                            onClick={() => remove(index)}
                    >
                        <i className="fa fa-times"/>
                    </button>;
                }
            }
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IChecklistModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [isDisabled, props, control, append, remove]);

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <ControlledList<IChecklistModel>
                uniqueListName={`${props.formName}@${props.fieldName}--contacts-list`}
                controllerName={props.formName}
                records={fields}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                listContextValues={{getCustomColumnComponent}}
                bordered={true}
                className="mb-1"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function ChecklistItemsField<Model extends IChecklistModel = IChecklistModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
